import { GoogleAnalyticsService } from '../../google-analytics.service';

/**
 * Last checkout session gtag has confirmed it sent.
 *
 * localStorage rather than sessionStorage: Stripe opens in a new tab
 * (SubscriptionConfirmationModalComponent) and the success URL is
 * bookmarkable, so a duplicate can easily arrive in a different session.
 */
export const SUBSCRIPTION_CONVERSION_SENT_KEY = 'subscription_conversion_sent';

/**
 * A purchase this browser knows about but has no confirmation was ever sent.
 *
 * This exists because of the shape of the success page. Stripe checkout is
 * opened with window.open, so `/user/subscription?payment-success=true` is a
 * popup tab sitting in front of the tab the buyer actually wants back, and
 * closing it is the normal thing to do. Getting from that URL to a hit
 * leaving the browser means: the shell, 420 KB of application JavaScript,
 * an AuthGuard, two lazy chunks, a backend round trip for the session
 * amount, and gtag.js having loaded. Seconds, on a tab with a short life
 * expectancy.
 *
 * Whatever is in here is a purchase that really happened and may never have
 * been reported, so any later page load in this browser will try again -
 * see replayPendingPurchaseConversion, wired up in AppComponent.
 */
export const SUBSCRIPTION_CONVERSION_PENDING_KEY =
  'subscription_conversion_pending';

/**
 * How long a pending record is worth replaying.
 *
 * An ad blocker means the confirmation callback never arrives, ever, so
 * without a ceiling the record would be re-fired on every page load until
 * the end of time. A week is long past the point where the server-side
 * upload is the right answer instead.
 */
export const PENDING_CONVERSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingPurchaseConversion {
  sessionId: string | null;
  plan: string;
  /** In major units (dollars), or null if the lookup never came back. */
  value: number | null;
  currency: string | null;
  /** Date.now() when the purchase was first seen. */
  at: number;
}

export function readPendingConversion(): PendingPurchaseConversion | null {
  try {
    const raw = localStorage.getItem(SUBSCRIPTION_CONVERSION_PENDING_KEY);
    if (!raw) return null;

    const pending = JSON.parse(raw) as PendingPurchaseConversion;
    // Hand-edited, half-written or from an older shape: a bad record must
    // read as "nothing pending" rather than throw on a page it has no
    // business being involved in.
    if (typeof pending?.at !== 'number' || typeof pending?.plan !== 'string')
      return null;

    return pending;
  } catch {
    return null;
  }
}

export function writePendingConversion(
  pending: PendingPurchaseConversion
): void {
  try {
    localStorage.setItem(
      SUBSCRIPTION_CONVERSION_PENDING_KEY,
      JSON.stringify(pending)
    );
  } catch {
    // Storage denied (private mode, full quota). The conversion is still
    // reported from this page; it just cannot be recovered if that fails.
  }
}

export function clearPendingConversion(): void {
  try {
    localStorage.removeItem(SUBSCRIPTION_CONVERSION_PENDING_KEY);
  } catch {
    // As above.
  }
}

export function conversionAlreadySent(sessionId: string): boolean {
  try {
    return localStorage.getItem(SUBSCRIPTION_CONVERSION_SENT_KEY) === sessionId;
  } catch {
    return false;
  }
}

/**
 * Called from gtag's `event_callback`, so it means the hit is on the wire
 * and not merely queued. That distinction is the whole point: the previous
 * version marked the session done before the amount lookup had even
 * returned, which turned a closed tab into a purchase that was recorded as
 * counted and never was, unrecoverably, because every later visit then
 * returned early on that same mark.
 */
export function markConversionSent(sessionId: string | null): void {
  clearPendingConversion();
  if (!sessionId) return;

  try {
    localStorage.setItem(SUBSCRIPTION_CONVERSION_SENT_KEY, sessionId);
  } catch {
    // Storage denied. transaction_id still deduplicates inside Ads.
  }
}

/**
 * Re-reports a purchase this browser never got confirmation of sending.
 *
 * Called once per app start from AppComponent, because the tab that could
 * not report is by then closed and the buyer's original tab is the one
 * still open. In practice that makes "the next page load" seconds away
 * rather than never.
 *
 * Safe to fire against a hit that did get out: both events carry the Stripe
 * session as `transaction_id`, which is what Ads and GA4 deduplicate on, and
 * a double count is the risk this whole file deliberately prefers to a
 * silent loss.
 */
export function replayPendingPurchaseConversion(
  ga: GoogleAnalyticsService
): void {
  const pending = readPendingConversion();
  if (!pending) return;

  if (pending.sessionId && conversionAlreadySent(pending.sessionId)) {
    clearPendingConversion();
    return;
  }

  if (Date.now() - pending.at > PENDING_CONVERSION_TTL_MS) {
    clearPendingConversion();
    return;
  }

  // Tracking must never break the page this happens to run on, and this one
  // runs on all of them.
  try {
    ga.logSubscriptionPurchase(
      pending.plan,
      pending.sessionId,
      pending.value,
      pending.currency,
      () => markConversionSent(pending.sessionId)
    );
  } catch (err) {
    console.error('[subscription-conversion] replay failed', err);
  }
}
